import {
  OnInit,
  OnDestroy,
  inject,
  DestroyRef,
  Injector,
  output,
  computed,
  signal,
  Component,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { FormUtils } from 'app/shared/form/form-utils';
import { ChapterService } from '../chapter/chapter.service';
import { FacetHooks } from '../utils/facet-hooks';
import { FacetCrudService } from './facet-crud.service';
import { MyHistoryUiService } from '../my-history-ui.service';
import { concatMap } from 'rxjs/operators';
import { filter, forkJoin, from, Observable, switchMap } from 'rxjs';
import { HttpUtils } from 'app/shared/http-utils';
import { FacetFilterService } from '../facet/facet-filter.service';
import { FilesService } from '../../shared/files.service';

@Component({
  standalone: true,
  template: '',
})
export abstract class FacetFormComponent<
    Model extends { id?: string | undefined },
    ModelForm extends { imageFile: File | File[]; suggestedImageFiles?: string; audio?: string | File },
    ModelSave extends { id?: string },
  >
  implements OnInit, OnDestroy
{
  abstract facetCrudService: FacetCrudService<Model>;
  abstract selected: Signal<Model | null>;
  abstract selectedImage: Signal<string | null | string[]>;
  abstract fileApiUrl: string;

  protected audioFileApiUrl: string = HttpUtils.getApiUrl('/files/audio');
  protected chapterService = inject(ChapterService);
  protected fb = inject(FormBuilder);
  protected destroyRef = inject(DestroyRef);
  protected uiService = inject(MyHistoryUiService);
  protected facetFilterService = inject(FacetFilterService);
  protected selectedFacet = this.chapterService.selectedFacet;
  protected selectedFacetType = this.chapterService.selectedFacetType;
  private injector = inject(Injector);
  protected filesService = inject(FilesService);

  submitted = output<ModelSave | null>();
  mode = computed(() => {
    return this.selected() ? 'Edit' : 'Create';
  });
  saveButtonText = computed(() => (this.selected() ? 'Save' : 'Create'));
  isEditMode = computed(() => this.mode() === 'Edit');
  form!: Signal<FormGroup>;
  facets = signal<FormArray<AbstractControl> | null>(null);
  facetId = signal<string | undefined>(undefined)

  abstract createFormGroup(facet: Model | null, imageFile?: string | null | string[]): FormGroup;

  abstract getSubmitPayload(facet: ModelForm): ModelSave;

  ngOnInit(): void {
    this.form = computed(() => {
      const selected = this.selected();
      const imageFile = this.selectedImage();
      return this.fb.nonNullable.group({
        facets: this.fb.array([this.createFormGroup(selected, imageFile)]),
      });
    });
    this.facets.set(FormUtils.getFormArray(this.form(), 'facets'));

    const selectedFacet = this.selectedFacet();
    if (selectedFacet) {
      this.facetCrudService.setSelected(selectedFacet.id);
    }
  }

  ngOnDestroy(): void {
    this.chapterService.setSelectedFacet(null);
    this.facetCrudService.setSelected(null);
  }

  onAddFacet() {
    this.facets.update((prev) => {
      const formGroup = this.createFormGroup(null, null);
      prev?.push(formGroup);
      return prev;
    });
  }

  onSubmit() {
    const { formValidityCheck, getFormArray } = FormUtils;
    if (!formValidityCheck(this.form())) {
      return;
    }

    const places = getFormArray(this.form(), 'facets');
    places.getRawValue().forEach((facet: ModelForm) => {
      return this.submitFormGroup(facet);
    });
  }

  onSubmitted(facet: ModelSave) {
    this.submitted.emit(facet);
  }

  submitFormGroup(facet: ModelForm) {
    const suggestedImageFile = facet.suggestedImageFiles?.length ? facet.suggestedImageFiles[0] : null;
    const payload = this.getSubmitPayload(facet);

    this.facetCrudService
      .save(payload, payload?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const requests: Observable<any>[] = [];
          const imageFile = facet.imageFile;

          this.facetId.set(res.id);

          if (facet.audio && this.filesService.isBlobUrl(facet.audio as string)) {
            const audioUpload$ = this.filesService.blobToFile(facet.audio as string, `${Date.now()}.wav`).pipe(
              switchMap((file) =>
                FacetHooks.uploadFile(this.injector, res.id as string, file, this.audioFileApiUrl)
              ),
              takeUntilDestroyed(this.destroyRef)
            );

            requests.push(audioUpload$);
          }

          if (imageFile instanceof File || facet.suggestedImageFiles) {
            const uploadFileFromUrl = !!suggestedImageFile;

            const imageUpload$ = FacetHooks.uploadFile(
              this.injector,
              res.id as string,
              imageFile as File,
              this.fileApiUrl,
              uploadFileFromUrl
            ).pipe(takeUntilDestroyed(this.destroyRef));

            requests.push(imageUpload$);
          }

          if (Array.isArray(imageFile)) {
            const imageArray = imageFile.filter(image => image instanceof File);

            if (imageArray.length) {
              const imageUploads$ = from(imageArray).pipe(
                filter(image => image instanceof File),
                concatMap((file) => FacetHooks.uploadFile(this.injector, res.id as string, file, this.fileApiUrl)),
                takeUntilDestroyed(this.destroyRef)
              );

              requests.push(imageUploads$);
            }
          }

          if (requests.length > 0) {
            forkJoin(requests).subscribe(() => this.onSubmitted(payload));
          } else {
            this.onSubmitted(payload);
          }
        },
      });
  }
}
